import { useTranslation } from "react-i18next";
import { useState } from "react";
import { GetCountries } from "../Country";
import Layout from "./common/Layout";
import FilteringSelect from "./common/FilteringSelect";
import Spinner from "./common/Spinner";
import CompanySearchResult from "./common/CompanySearchResult";
import Back from "./common/Back";
import companyIdentificationTypes from "../companyIdentification.json";
import { useApi } from "../store/hooks";
import { removeEmptyValues, isEmpty } from "../utils/utils";
import Toast from "./common/Toast";

const CompanyIdentification = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    countryCode: "",
    companyName: "",
    streetName: "",
    streetNumber: "",
    town: "",
    postalCode: "",
    companyIdentifier: {
      idType: "",
      idValue: "",
    },
  });

  const { searchCompany } = useApi();
  const [searchResult, setSearchResult] = useState([]);
  const [isPopinVisible, setIsPopinVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showMandatoryFieldsError, setShowMandatoryFieldsError] =
    useState(false);
  const [showMoreFields, setShowMoreFields] = useState(false);
  const dropdownCountries = GetCountries(i18n.language.toUpperCase());

  const toggleMoreFields = (e) => {
    e.preventDefault();
    setShowMoreFields((prevState) => !prevState);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (showMandatoryFieldsError) {
      setShowMandatoryFieldsError(false);
    }

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prevState) => ({
        ...prevState,
        [parent]: { ...prevState[parent], [child]: value },
      }));
    } else {
      setFormData((prevState) => {
        const newState = { ...prevState, [name]: value };
        if (name === "countryCode") {
          newState.companyIdentifier = {
            idType: "",
            idValue: "",
          };
        }
        return newState;
      });
    }
  };

  const validateForm = () => {
    if (!formData.countryCode) {
      return false;
    }
    const hasCompanyIdentifier =
      formData.companyIdentifier?.idType && formData.companyIdentifier?.idValue;
    const hasCompanyName = formData.companyName;
    return hasCompanyIdentifier || hasCompanyName;
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setShowMandatoryFieldsError(true);
      return;
    }
    setIsLoading(true);
    setShowMandatoryFieldsError(false);
    try {
      const payload = sanitizeFormData(formData);
      const response = await searchCompany(payload);
      setSearchResult(response);
      setIsPopinVisible(true);
      setIsLoading(false);
    } catch (error) {
      console.log("Error while searching company", error);
      setIsLoading(false);
    }
  };

  const sanitizeFormData = (data) => {
    const sanitizedData = { ...data };
    if (
      !isEmpty(data.companyIdentifier?.idType) &&
      isEmpty(data.companyIdentifier?.idValue)
    )
      delete sanitizedData.companyIdentifier;
    return removeEmptyValues(sanitizedData);
  };

  const closePopin = () => {
    setIsPopinVisible(false);
  };

  return (
    <Layout>
      <div className="az-title center">{t("companyIdentification.title")}</div>

      <Toast text={t("companyIdentification.toast.text")} />

      <div className="mt-2">
        <form className="d-flex row justify-content-center">
          <div className="row mt-1">
            <div className="col-sm-12 az-formfield mt-2">
              <FilteringSelect
                id="countryCode"
                name="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
                options={dropdownCountries}
                label={t("companyIdentification.country.placeHolder")}
                disabled={isLoading}
              />
            </div>

            <div className="az-formfield col-6 mt-2">
              <FilteringSelect
                id="companyIdentifier.idType"
                name="companyIdentifier.idType"
                value={formData.companyIdentifier?.idType || ""}
                onChange={handleChange}
                options={companyIdentificationTypes}
                label="companyIdentification.company.id.type"
                countryCode={formData.countryCode}
                disabled={!formData.countryCode || isLoading}
              />
            </div>

            <div className="az-formfield col-6 mt-2">
              <input
                type="text"
                id="companyIdentifier.idValue"
                name="companyIdentifier.idValue"
                value={formData.companyIdentifier?.idValue || ""}
                placeholder=" "
                onChange={handleChange}
                data-testid="input"
                className="az-formfield__input"
                disabled={!formData.companyIdentifier.idType || isLoading}
              />
              <label
                htmlFor="companyIdentifier.idValue"
                className="az-formfield__label"
              >
                {t("companyIdentification.id.label")}
              </label>
            </div>
          </div>

          <div className="col-sm-12 d-flex justify-content-center mt-2">
            <button
              type="button"
              onClick={toggleMoreFields}
              className={`az-back-label az-btn-toggle ${showMoreFields ? "" : "mb-4"}`}
            >
              {showMoreFields
                ? t("companyIdentification.less.btn")
                : t("companyIdentification.more.btn")}
            </button>
          </div>

          {showMoreFields && (
            <>
              <div className="row mt-1">
                <div className="az-formfield">
                  <input
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    placeholder=" "
                    onChange={handleChange}
                    className="az-formfield__input"
                    disabled={isLoading}
                  />
                  <label className="az-formfield__label" htmlFor="companyName">
                    {t("companyIdentification.companyName.label")}
                  </label>
                </div>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6 az-formfield">
                  <input
                    type="text"
                    id="streetNumber"
                    name="streetNumber"
                    value={formData.streetNumber}
                    placeholder=""
                    onChange={handleChange}
                    className="az-formfield__input"
                    disabled={isLoading}
                  />
                  <label htmlFor="streetNumber" className="az-formfield__label">
                    {t("companyIdentification.streetNumber.label")}
                  </label>
                </div>

                <div className="col-sm-12 col-md-6 az-formfield">
                  <input
                    type="text"
                    id="streetName"
                    name="streetName"
                    value={formData.streetName}
                    onChange={handleChange}
                    placeholder=" "
                    className="az-formfield__input"
                    disabled={isLoading}
                  />

                  <label htmlFor="streetName" className="az-formfield__label">
                    {t("companyIdentification.streetName.label")}
                  </label>
                </div>

                <div className="col-sm-12 col-md-6 az-formfield">
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    placeholder=" "
                    onChange={handleChange}
                    className="az-formfield__input"
                    disabled={isLoading}
                  />
                  <label htmlFor="postalCode" className="az-formfield__label">
                    {t("companyIdentification.postalCode.label")}
                  </label>
                </div>

                <div className="col-sm-12 col-md-6 az-formfield ">
                  <input
                    type="text"
                    id="town"
                    name="town"
                    value={formData.town}
                    placeholder=" "
                    onChange={handleChange}
                    className="az-formfield__input"
                    disabled={isLoading}
                  />
                  <label htmlFor="town" className="az-formfield__label">
                    {t("companyIdentification.city.label")}
                  </label>
                </div>
              </div>
            </>
          )}

          {showMandatoryFieldsError && (
            <div className="error-message-container mt-4">
              <span className="error-message not-received-span">
                {t("companyIdentification.fields.error")}
              </span>
            </div>
          )}

          <div className="mt-2 d-flex justify-content-start">
            {isLoading ? (
              <div className="w-100 az-btn disabled">
                <Spinner size={24} />
              </div>
            ) : (
              <button
                onClick={handleSearch}
                className="w-100 az-btn text-uppercase"
              >
                {t("companyIdentification.search.btn")}
              </button>
            )}
          </div>
        </form>

        {isPopinVisible && (
          <CompanySearchResult result={searchResult} onClose={closePopin} />
        )}
      </div>
      <Back message={t("back.label")} />
    </Layout>
  );
};

export default CompanyIdentification;
